/**
 * useSettledQuery — the query the wall asks for, once the field has paused (`answerPause.ts` holds the rule and the
 * timer; this is the wrapper React needs). `settleEpoch` is the count of the field's explicit answers (Enter, a pill or a
 * suggestion taken): a new count answers at once.
 */
import {useEffect, useRef, useState} from 'react'
import {answeredQuery, createSettler, type Settler} from './answerPause'

export function useSettledQuery(q: string, {sheet, settleEpoch}: {sheet: boolean; settleEpoch: number}): string {
    const [settled, setSettled] = useState(() => answeredQuery(q))
    const settler = useRef<Settler | null>(null)
    if (settler.current === null) settler.current = createSettler(q, setSettled)
    const seenEpoch = useRef(settleEpoch)

    useEffect(() => {
        const now = settleEpoch !== seenEpoch.current
        seenEpoch.current = settleEpoch
        settler.current?.change(q, {sheet, now})
        // `sheet` is read when the field changes, not a reason to answer again
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, settleEpoch])

    useEffect(() => () => settler.current?.cancel(), [])
    return settled
}
